import { useMutation, useQuery } from "@tanstack/react-query";
import type { Sort } from "@unbox/api";
import { handleError } from "@/api/error";
import { type Pagination, toQuery } from "@/api/pagination";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { client } from "../client";

export const useAdminProductUpdates = (pagination: Pagination, sort: Sort<"createdAt" | "publishedAt" | "title">) => {
  return useQuery({
    queryKey: ["admin", "product-updates", pagination.limit, pagination.offset, sort.column, sort.direction],
    queryFn: async () => {
      const res = await client.admin["product-updates"].$get({
        query: {
          ...toQuery(pagination),
          ...sort,
        },
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
  });
};

export const useAdminProductUpdate = (id: string) => {
  return useQuery({
    queryKey: ["admin", "product-updates", id],
    queryFn: async () => {
      const res = await client.admin["product-updates"][":id"].$get({ param: { id } });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!id,
  });
};

type CreateProductUpdatePayload = {
  title: string;
  copy: string;
  linkText?: string | null;
  linkUrl?: string | null;
  isPublished?: boolean;
  showAsPopup?: boolean;
};

export const useCreateProductUpdate = () => {
  return useMutation({
    mutationFn: async (payload: CreateProductUpdatePayload) => {
      const res = await client.admin["product-updates"].$post({ json: payload });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "product-updates"] });
    },
  });
};

type UpdateProductUpdatePayload = {
  id: string;
  title?: string;
  copy?: string;
  linkText?: string | null;
  linkUrl?: string | null;
  isPublished?: boolean;
  showAsPopup?: boolean;
};

export const useUpdateProductUpdate = () => {
  return useMutation({
    mutationFn: async ({ id, ...payload }: UpdateProductUpdatePayload) => {
      const res = await client.admin["product-updates"][":id"].$put({
        param: { id },
        json: payload,
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "product-updates"] });
    },
  });
};

export const useDeleteProductUpdate = () => {
  return useMutation({
    mutationFn: async (payload: { id: string }) => {
      const res = await client.admin["product-updates"][":id"].$delete({ param: { id: payload.id } });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "product-updates"] });
    },
  });
};

export const useUploadProductUpdateThumbnail = () => {
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      const res = await client.admin["product-updates"][":id"].thumbnail.$post({
        param: { id },
        form: { thumbnail: file },
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "product-updates"] });
    },
  });
};

export const useDeleteProductUpdateThumbnail = () => {
  return useMutation({
    mutationFn: async (payload: { id: string }) => {
      const res = await client.admin["product-updates"][":id"].thumbnail.$delete({ param: { id: payload.id } });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "product-updates"] });
    },
  });
};
